import { useState, useCallback, useRef, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import FolderTree from './FolderTree';
import ViewList from './ViewList';
import PageNavList from './PageNavList';

export default function MainLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [selectedViewId, setSelectedViewId] = useState(null);
  const [viewName, setViewName] = useState('');
  const [viewRefreshKey, setViewRefreshKey] = useState(0);
  const [folderRefreshKey, setFolderRefreshKey] = useState(0);

  const viewsRef = useRef(null);
  const foldersRef = useRef(null);
  const hasAutoSelected = useRef(false);
  const folderParam = searchParams.get('folder');

  // Check if we're on the dashboard (home) page
  const isDashboard = location.pathname === '/';

  const tryAutoSelect = useCallback(() => {
    if (!isDashboard) return;
    if (hasAutoSelected.current) return;
    if (viewsRef.current === null || foldersRef.current === null) return;

    hasAutoSelected.current = true;

    if (folderParam) {
      const folder = foldersRef.current.find(f => f.id === folderParam);
      if (folder) {
        setSelectedFolderId(folder.id);
        setFolderName(folder.name);
        setSearchParams({}, { replace: true });
        return;
      }
    }

    if (viewsRef.current.length > 0) {
      const firstView = viewsRef.current[0];
      setSelectedViewId(firstView.id);
      setViewName(firstView.name);
    } else if (foldersRef.current.length > 0) {
      const rootFolders = foldersRef.current.filter(f => f.parent_id === null);
      if (rootFolders.length > 0) {
        setSelectedFolderId(rootFolders[0].id);
        setFolderName(rootFolders[0].name);
      }
    }
  }, [folderParam, setSearchParams, isDashboard]);

  const handleViewsLoaded = useCallback((views) => {
    viewsRef.current = views;
    tryAutoSelect();
  }, [tryAutoSelect]);

  const handleFoldersLoaded = useCallback((folders) => {
    foldersRef.current = folders;
    tryAutoSelect();
  }, [tryAutoSelect]);

  const handleSelectFolder = useCallback((id) => {
    // Navigate to dashboard when selecting a folder
    if (location.pathname !== '/') {
      navigate('/');
    }
    setSelectedViewId(null);
    setViewName('');
    setSelectedFolderId(id);
    // Look up folder name from foldersRef
    if (id && foldersRef.current) {
      const folder = foldersRef.current.find(f => f.id === id);
      setFolderName(folder ? folder.name : '');
    } else {
      setFolderName('');
    }
  }, [location.pathname, navigate]);

  const handleSelectView = useCallback((id, name) => {
    // Navigate to dashboard when selecting a view
    if (location.pathname !== '/') {
      navigate('/');
    }
    setSelectedFolderId(null);
    setFolderName('');
    setSelectedViewId(id);
    setViewName(name || '');
  }, [location.pathname, navigate]);

  const handleSelectPage = useCallback(() => {
    // Clear folder/view selection when navigating to a page
    setSelectedFolderId(null);
    setFolderName('');
    setSelectedViewId(null);
    setViewName('');
  }, []);

  // Sidebar resizing
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('focusly-sidebar-width');
    return saved ? Number(saved) : 350;
  });
  const isResizing = useRef(false);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      const newWidth = Math.min(Math.max(e.clientX, 200), 600);
      setSidebarWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      localStorage.setItem('focusly-sidebar-width', String(sidebarWidth));
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [sidebarWidth]);

  // Reset auto-selection when navigating back to dashboard
  useEffect(() => {
    if (isDashboard && !selectedFolderId && !selectedViewId) {
      hasAutoSelected.current = false;
      tryAutoSelect();
    }
  }, [isDashboard, selectedFolderId, selectedViewId, tryAutoSelect]);

  return (
    <div className="d-flex" style={{ height: 'calc(100vh - 56px)' }}>
      <div className="app-sidebar overflow-auto" style={{ width: sidebarWidth, minWidth: 200, maxWidth: 600, flexShrink: 0 }}>
        <PageNavList onSelectPage={handleSelectPage} />
        <div className="border-top">
          <ViewList
            selectedViewId={isDashboard ? selectedViewId : null}
            onSelectView={handleSelectView}
            onViewSaved={() => setViewRefreshKey((k) => k + 1)}
            onViewsLoaded={handleViewsLoaded}
          />
        </div>
        <div className="border-top">
          <FolderTree
            selectedFolderId={isDashboard ? selectedFolderId : null}
            onSelectFolder={handleSelectFolder}
            onFoldersLoaded={handleFoldersLoaded}
            onFoldersChanged={() => setFolderRefreshKey((k) => k + 1)}
          />
        </div>
      </div>
      <div
        className="sidebar-resize-handle"
        onMouseDown={handleMouseDown}
      />
      <div className="flex-grow-1 overflow-auto">
        <Outlet context={{
          selectedFolderId,
          folderName,
          selectedViewId,
          viewName,
          viewRefreshKey,
          folderRefreshKey,
          setSelectedFolderId,
          setFolderName,
        }} />
      </div>
    </div>
  );
}
